// ╔══════════════════════════════════════════════════════════════╗
// ║  Worker Monthly Report                                         ║
// ║  /production/workers/:id/monthly?month=YYYY-MM                ║
// ║                                                                ║
// ║  Shows the worker's daily production for a month, including   ║
// ║  WHICH STAGE they worked on each day. Aggregated by stage     ║
// ║  for the full picture.                                        ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, downloadFile } from '../../lib/api.js';
import { useToast } from '../../components/Toast.jsx';
import { fmtAR } from '../../lib/format.js';
import { Avatar, Badge, Btn, Card, Field, Input } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';

export default function WorkerMonthlyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [month, setMonth] = useState(
    () => searchParams.get('month') ?? new Date().toISOString().slice(0, 7),
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'worker-monthly', id, month],
    queryFn: () =>
      api
        .get(`/reports/worker-monthly/${id}`, { params: { month } })
        .then((r) => r.data),
  });

  function changeMonth(m) {
    setMonth(m);
    setSearchParams({ month: m });
  }

  function exportCsv() {
    if (!data) return;
    const rows = data.days.filter((d) => d.worked);
    if (rows.length === 0) {
      toast.push({ tone: 'warn', title: 'لا توجد إدخالات للتصدير' });
      return;
    }
    const headers = ['اليوم', 'التاريخ', 'المرحلة', 'الهدف', 'الفعلي', 'الفرق', 'الحالة'];
    const lines = [headers.join(',')];
    for (const d of rows) {
      lines.push([
        d.day,
        d.date,
        `"${d.stageName ?? ''}"`,
        d.target,
        d.actual,
        d.delta,
        d.status === 'approved' ? 'معتمد' : d.status === 'rejected' ? 'مرفوض' : 'تحت المراجعة',
      ].join(','));
    }
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `worker_${id}_${month}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  if (isLoading) {
    return (
      <div className="p-6 grid place-items-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-4 border-brand-200 border-t-brand-500 animate-spin" />
      </div>
    );
  }
  if (error || !data?.worker) {
    return (
      <div className="p-8 text-center">
        <Icon name="alert" size={48} className="mx-auto text-rose-400 mb-3" />
        <h2 className="font-bold text-lg">لم يتم العثور على العامل</h2>
        <Btn variant="outline" className="mt-4" onClick={() => navigate('/production/workers')}>
          رجوع
        </Btn>
      </div>
    );
  }

  const { worker, days, stages, totals } = data;
  const eff = totals.target > 0 ? Math.round((totals.produced / totals.target) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      {/* Breadcrumbs */}
      <nav className="text-xs text-ink-400 flex items-center gap-1.5 flex-wrap">
        <Link to="/" className="hover:text-brand-500">الرئيسية</Link>
        <Icon name="chevL" size={11} />
        <Link to="/production/workers" className="hover:text-brand-500">عمال الإنتاج</Link>
        <Icon name="chevL" size={11} />
        <span className="text-ink-700 dark:text-ink-100 font-bold truncate">
          تقرير شهرى — {worker.name}
        </span>
      </nav>

      {/* Hero */}
      <Card className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Avatar name={worker.name} size={64} className="text-xl" />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-black truncate">{worker.name}</h1>
            <div className="text-sm text-ink-500 dark:text-ink-300 mt-1">
              <span className="font-mono">{worker.id}</span>
              {worker.employeeId && (
                <>
                  <span className="mx-2 text-ink-300">·</span>
                  <span>كود الموظف: <span className="font-mono">{worker.employeeId}</span></span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Field label="الشهر">
              <Input
                type="month"
                value={month}
                onChange={(e) => changeMonth(e.target.value)}
                dir="ltr"
                className="w-44"
              />
            </Field>
            <Btn variant="outline" icon="download" onClick={exportCsv}>
              تصدير Excel
            </Btn>
            <Btn variant="ghost" icon="arrowR" onClick={() => navigate(-1)}>رجوع</Btn>
          </div>
        </div>
      </Card>

      {/* Totals KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard label="أيام العمل" value={fmtAR(totals.workedDays)} sub="يوم" />
        <KPICard label="الإنتاج الكلى" value={fmtAR(totals.produced)} sub="قطعة" tone="brand" />
        <KPICard label="الهدف الكلى" value={fmtAR(totals.target)} sub="قطعة" />
        <KPICard label="نسبة التحقيق" value={`${eff}%`} tone={eff >= 100 ? 'success' : 'warn'} />
        <KPICard
          label="صافى المكافأة"
          value={fmtAR(totals.delta)}
          sub="جنيه"
          tone={totals.delta >= 0 ? 'success' : 'danger'}
        />
      </div>

      {/* Stage breakdown */}
      {stages.length > 0 && (
        <Card className="p-5">
          <h3 className="font-bold text-base mb-3">
            توزيع الأيام على المراحل ({fmtAR(stages.length)} مرحلة)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stages.map((s) => (
              <div
                key={s.stageId}
                className="p-3 rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 bg-white dark:bg-ink-900"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="inline-flex items-center gap-1.5 font-bold text-sm">
                    <span
                      className="w-2.5 h-2.5 rounded"
                      style={{ backgroundColor: s.stageColor ?? '#cc9c46' }}
                    />
                    {s.stageName}
                  </span>
                  <Badge tone="brand">{fmtAR(s.days)} يوم</Badge>
                </div>
                <div className="text-lg font-extrabold tabular">
                  {fmtAR(s.produced)} / {fmtAR(s.target)}
                </div>
                <div className="text-[11px] text-ink-400">
                  {s.target > 0 ? Math.round((s.produced / s.target) * 100) : 0}% تحقيق
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Daily calendar grid */}
      <Card className="p-5">
        <h3 className="font-bold text-base mb-4">التفصيل اليومى — {month}</h3>
        <div className="grid grid-cols-7 gap-1.5 mb-1.5 text-center text-[10px] font-bold text-ink-400">
          {['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'].map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {/* Empty cells before day 1 */}
          {(() => {
            const firstDay = new Date(month + '-01').getDay();
            return Array.from({ length: firstDay }).map((_, i) => <div key={`pad-${i}`} />);
          })()}
          {days.map((d) => (
            <DayCell key={d.date} day={d} />
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px]">
          <LegendDot color="#10b981" label="حقّق ≥100%" />
          <LegendDot color="#cc9c46" label="80-99%" />
          <LegendDot color="#f59e0b" label="<80%" />
          <LegendDot color="#6b728020" label="لم يعمل" border />
        </div>
      </Card>

      {/* Daily table */}
      <Card className="overflow-hidden">
        <div className="p-5 pb-3">
          <h3 className="font-bold">سجل الأيام التفصيلى</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {['اليوم', 'التاريخ', 'المرحلة', 'الهدف', 'الفعلي', 'الأثر', 'الحالة'].map(
                  (h, i) => (
                    <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {days.filter((d) => d.worked).length === 0 && (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-ink-400">
                    لا توجد إدخالات لهذا الشهر
                  </td>
                </tr>
              )}
              {days
                .filter((d) => d.worked)
                .map((d) => (
                  <tr key={d.date} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                    <td className="px-4 py-2.5 font-mono text-xs tabular">{d.day}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{d.date}</td>
                    <td className="px-4 py-2.5">
                      {d.stageName ? (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold"
                          style={{
                            backgroundColor: `${d.stageColor ?? '#cc9c46'}20`,
                            color: d.stageColor ?? '#cc9c46',
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: d.stageColor ?? '#cc9c46' }} />
                          {d.stageName}
                        </span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 tabular font-semibold">{fmtAR(d.target)}</td>
                    <td className="px-4 py-2.5 tabular font-extrabold">{fmtAR(d.actual)}</td>
                    <td className="px-4 py-2.5">
                      {Number(d.delta) > 0 ? (
                        <Badge tone="success">+{fmtAR(d.delta)} ج</Badge>
                      ) : Number(d.delta) < 0 ? (
                        <Badge tone="danger">{fmtAR(d.delta)} ج</Badge>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge
                        tone={
                          d.status === 'approved'
                            ? 'success'
                            : d.status === 'rejected'
                            ? 'danger'
                            : 'warn'
                        }
                        dot
                      >
                        {d.status === 'approved'
                          ? 'معتمد'
                          : d.status === 'rejected'
                          ? 'مرفوض'
                          : 'تحت المراجعة'}
                      </Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function KPICard({ label, value, sub, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-100',
    brand: 'text-brand-600 dark:text-brand-300',
    success: 'text-emerald-600',
    warn: 'text-amber-600',
    danger: 'text-rose-600',
  };
  return (
    <div className="p-3 rounded-xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[10px] uppercase text-ink-400 font-bold">{label}</div>
      <div className={`text-xl font-black tabular mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function DayCell({ day }) {
  if (!day.worked) {
    return (
      <div className="aspect-square rounded-md ring-1 ring-ink-100 dark:ring-ink-800 p-1 flex items-start text-[10px] text-ink-400">
        {day.day}
      </div>
    );
  }
  const eff = day.target > 0 ? day.actual / day.target : 0;
  const color = eff >= 1 ? '#10b981' : eff >= 0.8 ? '#cc9c46' : '#f59e0b';
  return (
    <div
      title={`${day.date} · ${day.stageName ?? '—'} · ${day.actual}/${day.target}`}
      className="aspect-square rounded-md p-1 flex flex-col justify-between cursor-help hover:ring-2 hover:ring-brand-400 transition"
      style={{ backgroundColor: color, opacity: 0.85 }}
    >
      <span className="text-[10px] font-bold text-white drop-shadow">{day.day}</span>
      <span className="text-[10px] font-extrabold tabular text-white drop-shadow">{day.actual}</span>
    </div>
  );
}

function LegendDot({ color, label, border }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`w-3 h-3 rounded ${border ? 'ring-1 ring-ink-300' : ''}`}
        style={{ backgroundColor: color }}
      />
      <span className="text-ink-500">{label}</span>
    </span>
  );
}
